import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    CheckboxIcon,
    StyledLabel,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { useSnapshot } from "valtio";

import {
    setLimitToast,
    store as userStore,
} from "@components/VerticalLayout/store";
import { setKey } from "./store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../api/hooks";

type ConfirmationArchiveType = {
    setIsDataUpdated: Function;
    setIsModalArchiveOpened: Function;
    isModalArchiveOpened: boolean;
    selectedRows: any;
    isArchivedTemplateClicked: boolean;
    dataTable: any;
    setIsModalLoadingOpened: Function;
    setModalLoadingData: any;
};

export function ConfirmationArchiveTemplate({
    setIsDataUpdated,
    setIsModalArchiveOpened,
    isModalArchiveOpened,
    selectedRows,
    isArchivedTemplateClicked,
    setIsModalLoadingOpened,
    setModalLoadingData,
}: ConfirmationArchiveType): JSX.Element {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const { userID, franchiseID, operatorID, oneShop } = useSnapshot(userStore);
    const [
        isValidateButtonDisabled,
        setIsValidateButtonDisabled,
    ] = React.useState(false);
    const uuidUser = localStorage.getItem("uuidUser");

    async function archiveTemplate() {
        let apiUrlPlan = `${process.env.REACT_APP_API_V2_URL}/settings/archiveProject`;
        toast.dismiss();
        setIsModalArchiveOpened(false);
        setModalLoadingData({
            title: "Archiving in progress",
            message: "Template archiving is in progress.",
        });
        setIsModalLoadingOpened(true);
        if (selectedRows.length > 0) {
            try {
                let ids: any = {};
                for (let el of selectedRows) {
                    ids[el.projectId] = el[t("Designation")];
                }

                let savedData = {
                    userId: userID,
                    franchiseId: franchiseID,
                    shopId: "0",
                    operatorId: operatorID,
                    uuidUser: uuidUser,
                    etat: "Archivage",
                    projectIds: ids,
                };

                const response = await fetchGlobal<any>(apiUrlPlan, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(savedData),
                });
                if (response) {
                    setIsModalLoadingOpened(false);
                    toast.success(
                        `${t("Template have been successfully archived")!}`,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 2000,
                            theme: "colored",
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                    setKey(Math.floor(Math.random() * 10000));
                } else {
                    setIsModalLoadingOpened(false);
                    toast.error(`Failed to save data: Unknown error`, {
                        position: "top-right",
                        autoClose: 3000,
                    });
                }
            } catch (e: any) {
                setIsModalLoadingOpened(false);
                toast.error(`${t("There's an error")}!`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    }

    async function deleteShop() {
        let apiUrlPlan = `${process.env.REACT_APP_API_V2_URL}/settings/projects/delete`;
        toast.dismiss();
        let localData: any = [];

        selectedRows.forEach(async (el: any) => {
            el.shopKey.forEach((e: any) => {
                let savedData = {
                    userId: userID,
                    franchiseId: franchiseID,
                    shopId: e,
                    projectId: "PROJECT",
                    operatorId: operatorID,
                    project_id: el.projectId,
                };
                localData.push(
                    fetchGlobal(apiUrlPlan, {
                        method: "delete",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(savedData),
                    })
                );
            });
        });
        try {
            mutate(
                apiUrlPlan,
                await Promise.all(localData).then((result: any) => {
                    let errors: boolean[] = result.map((el: any) => el.error);
                    if (!errors.includes(true)) {
                    }
                })
            );
        } catch (e: any) {
            toast.error(`${t("There's an error")}!`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    async function activeTemplate() {
        toast.dismiss();
        setIsModalArchiveOpened(false);
        setModalLoadingData({
            title: "Unarchiving in progress",
            message: "Template unarchiving is in progress.",
        });
        setIsModalLoadingOpened(true);

        toast.info(
            t(
                "Please note that the template has been updated in accordance with the general settings."
            ),
            {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            }
        );

        if (selectedRows.length === 0) {
            toast.warn(t("No rows selected"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            setIsModalLoadingOpened(false);
            return;
        }

        try {
            let localData = {
                userId: userID,
                franchiseId: franchiseID,
                shopId: "0",
                operatorId: operatorID,
                uuidUser: uuidUser,
                etat: "Archivage",
                projectIds: selectedRows.map((el: any) => el.projectId),
            };
            await postSavedData(localData);
        } catch (error) {
            console.error("❌ Unexpected error in activeTemplate:", error);
        } finally {
            setIsModalArchiveOpened(false);
        }
    }

    async function postSavedData(savedData: any) {
        const apiUrlPlan = `${process.env.REACT_APP_API_V2_URL}/settings/unarchiveProject`;

        try {
            const result = await fetchGlobal<any>(apiUrlPlan, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(savedData),
            });

            // Show success or error toast based on API response

            if (result.inactiveItems.length > 0) {
                setLimitToast(2);
                let message: string = "";
                result.inactiveItems = [...new Set(result.inactiveItems)];

                result.inactiveItems.forEach((item: any) => {
                    message += `, ${item}`;
                });
                message = message.substring(1);
                ErrorToast(
                    `${t(
                        "Please note, the unarchiving could not be carried out successfully. Please check your general settings:",
                        { items: message }
                    )}`
                );
            }
            setIsModalLoadingOpened(false);
            toast.success(
                `${t("Template have been successfully activated")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
            setKey(Math.floor(Math.random() * 10000));
        } catch (e: any) {
            setIsModalLoadingOpened(false);
            console.error("Error posting saved data:", e);
            toast.error(
                `Failed to save data: ${e.message || "Unknown error"}`,
                {
                    position: "top-right",
                    autoClose: 3000,
                }
            );
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalArchiveOpened} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalArchiveOpened(!isModalArchiveOpened);
                    setIsDataUpdated(true);
                }}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {!isArchivedTemplateClicked
                        ? selectedRows.length > 1
                            ? t("Please confirm templates archive")
                            : t("Please confirm template archive")
                        : t("Please confirm template active")}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    className="cmn_btn_cancelAlert"
                    onClick={() => {
                        setIsModalArchiveOpened(!isModalArchiveOpened);
                        setIsDataUpdated(true);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2 cmn_btn_validateAlert"
                    variant="primary"
                    disabled={isValidateButtonDisabled}
                    onClick={async () => {
                        setIsValidateButtonDisabled(true);
                        if (!isArchivedTemplateClicked) {
                            await archiveTemplate();
                            if (oneShop) deleteShop();
                        } else {
                            await activeTemplate();
                        }
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
